/**
 * Fire-and-forget calls to the Pages Functions.
 *
 * Every function here swallows its own errors. Recording data must never
 * break the experience: if the backend is missing or down, the app keeps
 * working and we simply lose the row.
 */
import { isPersonaCard, type Profile, type SwipeEntry } from "@lantern/core";

async function post<T>(url: string, body: unknown): Promise<T | null> {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/** Opens a session when swiping begins, so abandoned runs still appear in the funnel. */
export async function openSession(input: {
  tripMonth: number;
  tripDays: number;
  kids: boolean;
}): Promise<string | null> {
  const res = await post<{ sessionId: string | null }>("/api/session", input);
  return res?.sessionId ?? null;
}

/** Records the swipe log and computed read once results exist. */
export async function completeSession(
  sessionId: string | null,
  log: SwipeEntry[],
  profile: Profile,
  persona: unknown,
  axes: unknown,
  uni: unknown,
): Promise<void> {
  if (!sessionId) return;
  await post("/api/complete", {
    sessionId,
    swipes: log.map((e) => ({
      cardId: e.card.id,
      cardType: isPersonaCard(e.card) ? "persona" : "experience",
      direction: e.dir,
      dwellMs: e.ms,
    })),
    profile: {
      axes,
      uni,
      persona,
      mbti: profile.travelType,
      archetype: profile.arch.name,
      routeKey: profile.route.name,
      sidekickName: profile.sidekick.name,
      matchPct: profile.matchPct,
      nSignals: profile.signals,
    },
  });
}

export interface ReserveResult {
  ok: boolean;
  recorded?: boolean;
  notified?: boolean;
}

/** Submits booking intent. Returns null when the request never landed. */
export function reserve(input: {
  sessionId: string | null;
  email: string;
  sidekick: string;
  route: string;
  days: number;
  quote: number;
  deposit: number;
}): Promise<ReserveResult | null> {
  return post<ReserveResult>("/api/reserve", input);
}
